import React, { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowRight, ChevronDown } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useLang } from "../contexts/LanguageContext";
import { PHONE, PHONE_TEL, ADDRESS, EMAIL, COUNTRY_CODES } from "../data/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
    name: "",
    email: "",
    dialCode: "+30",
    phone: "",
    date: "",
    time: "19:00",
    guests: 2,
    message: "",
};

const ContactPage = () => {
    const { t } = useLang();
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: name === "guests" ? Number(value) : value }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { dialCode, phone, ...rest } = form;
            const payload = { ...rest, phone: `${dialCode} ${phone}`.trim() };
            await axios.post(`${API}/reservations`, payload);
            toast.success(t("contact.success"));
            setForm(initial);
        } catch (err) {
            console.error(err);
            toast.error(t("contact.error"));
        } finally {
            setLoading(false);
        }
    };

    const fieldCls =
        "w-full px-4 py-3 bg-white border border-aegean-200 rounded-sm text-ink placeholder:text-ink/40 focus:outline-none focus:border-aegean-400 focus:ring-2 focus:ring-aegean-300/40 transition";
    const labelCls =
        "block font-display uppercase tracking-[0.22em] text-xs text-aegean-700 mb-2";

    return (
        <div data-testid="contact-page" className="pt-32 pb-24 bg-ivory min-h-screen">
            <Toaster position="top-center" richColors />
            <section className="container-x text-center mb-12">
                <p className="eyebrow">{t("nav.contact")}</p>
                <h1 className="headline text-aegean-700 text-6xl md:text-7xl lg:text-8xl mt-3">
                    {t("contact.title")}
                </h1>
                <p className="mt-5 text-ink/70 max-w-xl mx-auto">{t("contact.subtitle")}</p>
            </section>

            <section className="container-x grid lg:grid-cols-3 gap-10">
                <form
                    onSubmit={onSubmit}
                    data-testid="reservation-form"
                    className="lg:col-span-2 bg-white p-8 md:p-12 shadow-xl border border-aegean-100"
                >
                    <div className="grid md:grid-cols-2 gap-5">
                        <div>
                            <label className={labelCls} htmlFor="name">{t("contact.name")} *</label>
                            <input
                                id="name"
                                name="name"
                                data-testid="form-name"
                                required
                                value={form.name}
                                onChange={onChange}
                                className={fieldCls}
                            />
                        </div>
                        <div>
                            <label className={labelCls} htmlFor="email">{t("contact.email")} *</label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                data-testid="form-email"
                                required
                                value={form.email}
                                onChange={onChange}
                                className={fieldCls}
                            />
                        </div>

                        {/* Phone with country code dropdown */}
                        <div className="md:col-span-2">
                            <label className={labelCls} htmlFor="phone">{t("contact.phone")} *</label>
                            <div className="flex gap-2">
                                <div className="relative shrink-0">
                                    <select
                                        name="dialCode"
                                        data-testid="form-dial-code"
                                        value={form.dialCode}
                                        onChange={onChange}
                                        className={`${fieldCls} appearance-none pr-9 cursor-pointer min-w-[150px]`}
                                        aria-label="Country code"
                                    >
                                        {COUNTRY_CODES.map((c) => (
                                            <option
                                                key={`${c.iso}-${c.code}`}
                                                value={c.code}
                                            >
                                                {c.iso} {c.code} — {c.name}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown
                                        size={16}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-aegean-500"
                                    />
                                </div>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    inputMode="tel"
                                    data-testid="form-phone"
                                    required
                                    placeholder="697 414 1414"
                                    value={form.phone}
                                    onChange={onChange}
                                    className={`${fieldCls} flex-1`}
                                />
                            </div>
                        </div>

                        <div>
                            <label className={labelCls} htmlFor="guests">{t("contact.guests")} *</label>
                            <input
                                id="guests"
                                name="guests"
                                type="number"
                                min="1"
                                max="30"
                                data-testid="form-guests"
                                required
                                value={form.guests}
                                onChange={onChange}
                                className={fieldCls}
                            />
                        </div>
                        <div>
                            <label className={labelCls} htmlFor="date">{t("contact.date")} *</label>
                            <input
                                id="date"
                                name="date"
                                type="date"
                                data-testid="form-date"
                                required
                                value={form.date}
                                onChange={onChange}
                                className={fieldCls}
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className={labelCls} htmlFor="time">{t("contact.time")} *</label>
                            <input
                                id="time"
                                name="time"
                                type="time"
                                data-testid="form-time"
                                required
                                value={form.time}
                                onChange={onChange}
                                className={fieldCls}
                            />
                        </div>
                    </div>

                    <div className="mt-5">
                        <label className={labelCls} htmlFor="message">{t("contact.message")}</label>
                        <textarea
                            id="message"
                            name="message"
                            data-testid="form-message"
                            value={form.message}
                            onChange={onChange}
                            rows={4}
                            className={fieldCls}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="form-submit"
                        className="btn-primary mt-8 w-full md:w-auto"
                    >
                        {loading ? t("contact.sending") : t("contact.submit")}{" "}
                        <ArrowRight size={16} />
                    </button>
                </form>

                <aside className="bg-aegean-700 text-white p-8 md:p-10 flex flex-col">
                    <h3 className="font-display uppercase tracking-[0.28em] text-xs text-aegean-200 mb-6">
                        {t("contact.or")}
                    </h3>
                    <div className="space-y-6 flex-1">
                        <div className="flex items-start gap-3">
                            <Phone size={20} className="text-aegean-300 mt-1" />
                            <a
                                href={`tel:${PHONE_TEL}`}
                                data-testid="aside-phone-link"
                                className="text-lg font-medium hover:text-aegean-300"
                            >
                                {PHONE}
                            </a>
                        </div>
                        <div className="flex items-start gap-3">
                            <MapPin size={20} className="text-aegean-300 mt-1" />
                            <div>
                                {ADDRESS}
                                <br />
                                Lemnos, Greece
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Mail size={20} className="text-aegean-300 mt-1" />
                            <a
                                href={`mailto:${EMAIL}`}
                                data-testid="aside-email-link"
                                className="hover:text-aegean-300 break-all"
                            >
                                {EMAIL}
                            </a>
                        </div>
                    </div>
                    <div className="mt-6 text-sm text-white/60">
                        {t("location.hours")}
                    </div>
                </aside>
            </section>
        </div>
    );
};

export default ContactPage;
